import { formatPosition } from '@felix/domain';
import { Button, UnstyledButton } from '@mantine/core';
import {
  IconAlertTriangle,
  IconAnchor,
  IconArrowBackUp,
  IconCheck,
  IconEdit,
  IconExternalLink,
  IconFile,
  IconFileText,
  IconLockOpen,
  IconSend,
  IconShieldCheck,
  IconSun,
  IconTool,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDay } from '../../format.js';
import { StatusBadge } from '../../StatusBadge.js';
import { ReturnedBanner } from '../returned/Returned.js';
import classes from './Daily.module.css';
import { fieldLabelKey, noonBadge, REPORT_FIELDS, reportBadge } from './logic.js';
import { shipDateTime } from './parts.js';
import type { DailyNoonReport, DailyReportDetail } from './types.js';

export const KIND_ICONS: Record<string, ReactNode> = {
  noon: <IconSun size={20} />,
  daily: <IconFileText size={20} />,
  defect_observation: <IconAlertTriangle size={20} />,
  safety: <IconShieldCheck size={20} />,
  port: <IconAnchor size={20} />,
};

/** Alanın gösterilen değeri: liste değeri çevrilir, gün biçimlenir. */
function valueText(
  t: (key: string) => string,
  key: string,
  value: unknown,
  labels?: string,
): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? t('daily.details.yes') : t('daily.details.no');
  if (labels) return t(`daily.${labels}.${String(value)}`);
  if (key === 'deadline' || key === 'nextDue') return formatDay(String(value));
  return String(value);
}

export type DetailAction = 'edit' | 'submit' | 'discard' | 'approve' | 'return' | 'reopen';

/**
 * Seçili raporun paneli (ortağın taslağındaki sağ panel): tür, durum, tarih ve alanlar, özet, ekler,
 * revizyon geçmişi ve onay izi; eylemler `can`'a göre. Gönderilen rapor değişmez (ADR 0051 §6).
 */
export function ReportDetail({
  detail,
  onClose,
  onAction,
  fileUrl,
  onConvert,
  onOpenDefect,
}: {
  detail: DailyReportDetail;
  onClose: () => void;
  onAction: (action: DetailAction) => void;
  fileUrl?: ((id: string) => string) | undefined;
  /** Gözlemi arızaya dönüştürür (gemi; ADR 0060 §7). */
  onConvert?: (() => void) | undefined;
  /** Gözlemden açılmış arızayı açar. */
  onOpenDefect?: ((id: string) => void) | undefined;
}) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`daily.detail.${key}`, options ?? {});
  const badge = reportBadge(detail);
  const name = (id: string | null | undefined) => (id ? (detail.names[id] ?? '—') : '—');
  const sections =
    detail.kind === 'daily'
      ? (['deck', 'engine', 'safety', 'environment', 'remarks'] as const)
      : [];
  const last = detail.submissions.at(-1);
  const sections2 = sections.filter(
    (s) => typeof detail.data[s] === 'string' && detail.data[s] !== '',
  );
  return (
    <section className={classes.card} aria-labelledby="daily-detail-title" data-panel>
      <div className={classes.detailsHead}>
        <span className={classes.kindIcon} data-kind={detail.kind} aria-hidden>
          {KIND_ICONS[detail.kind]}
        </span>
        <h2 id="daily-detail-title" className={classes.detailsLabel}>
          {t(`daily.kinds.${detail.kind}`)}
        </h2>
        <StatusBadge tone={badge.tone}>{t(`daily.reportStatuses.${badge.key}`)}</StatusBadge>
        <UnstyledButton className={classes.iconButton} onClick={onClose} aria-label={k('close')}>
          <IconX size={18} />
        </UnstyledButton>
      </div>
      {/* İade bandı (ADR 0116 Ek): iade eden, zaman, gerekçe */}
      {detail.status === 'returned' ? (
        <ReturnedBanner
          notice={detail.returnNotice}
          offsetMin={detail.tzOffsetMin}
          hint={Boolean(detail.can.edit)}
        />
      ) : null}
      <dl className={classes.facts}>
        <dt>{k('dateTime')}</dt>
        <dd>{shipDateTime(detail.reportedAt, detail.tzOffsetMin)}</dd>
        <dt>{k('department')}</dt>
        <dd>{detail.department ? t(`daily.departments.${detail.department}`) : '—'}</dd>
        {REPORT_FIELDS[detail.kind]
          .filter((spec) => spec.type !== 'textarea')
          .map((spec) => (
            <Fact
              key={spec.key}
              label={t(`daily.form.${fieldLabelKey(spec.key)}`)}
              value={valueText(
                t,
                spec.key,
                detail.data[spec.key],
                'labels' in spec ? spec.labels : undefined,
              )}
            />
          ))}
        {detail.revision > 0 ? (
          <>
            <dt>{t('daily.detail.history')}</dt>
            <dd>{k('revision', { no: detail.revision })}</dd>
          </>
        ) : null}
      </dl>
      <div className={classes.noteBox}>
        <h4>{k('summary')}</h4>
        <p>{detail.summary || '—'}</p>
        {REPORT_FIELDS[detail.kind]
          .filter((spec) => spec.type === 'textarea' && detail.data[spec.key])
          .map((spec) => (
            <div key={spec.key}>
              <h4>{t(`daily.form.${spec.key}`)}</h4>
              <p>{String(detail.data[spec.key])}</p>
            </div>
          ))}
        {sections2.map((s) => (
          <div key={s}>
            <h4>{t(`daily.form.${s}`)}</h4>
            <p>{String(detail.data[s])}</p>
          </div>
        ))}
      </div>
      <h3 className={classes.subTitle}>{k('attachments')}</h3>
      {detail.files.length === 0 ? (
        <p className={classes.muted}>{k('noAttachments')}</p>
      ) : (
        <ul className={classes.files}>
          {detail.files.map((f) => (
            <li key={f.id}>
              <IconFile size={16} aria-hidden />
              {fileUrl && f.officeState === 'verified' ? (
                <a
                  className={classes.ellipsis}
                  href={fileUrl(f.id)}
                  target="_blank"
                  rel="noreferrer"
                >
                  {f.name}
                </a>
              ) : (
                <span className={classes.ellipsis}>{f.name}</span>
              )}
              {f.officeState && f.officeState !== 'verified' ? (
                <small>{k('fileOnVessel')}</small>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      <h3 className={classes.subTitle}>{k('history')}</h3>
      <ul className={classes.trail}>
        {detail.revisions.map((r) => (
          <li key={r.revisionNo}>
            {k('historyRow', {
              no: r.revisionNo,
              at: shipDateTime(r.at, detail.tzOffsetMin),
              by: name(r.by),
            })}
            {r.reason ? <span className={classes.muted}> — {r.reason}</span> : null}
          </li>
        ))}
        {detail.decisions.map((d) => (
          <li key={`${d.submissionId}-${d.step}-${d.at}`}>
            {d.decision === 'approved'
              ? k('approvedBy', {
                  by: name(d.by),
                  role: detail.roles[d.roleId] ?? '—',
                  at: shipDateTime(d.at, detail.tzOffsetMin),
                })
              : k(d.step > (detail.ruleSteps ?? 0) ? 'reopenedBy' : 'returnedBy', {
                  by: name(d.by),
                  at: shipDateTime(d.at, detail.tzOffsetMin),
                  reason: d.reason ?? '',
                })}
          </li>
        ))}
        {detail.pending ? (
          <li className={classes.pendingRow}>
            {k('awaiting', { role: detail.roles[detail.pending.roleId ?? ''] ?? '—' })}
          </li>
        ) : null}
        {detail.revisions.length === 0 && last === undefined ? <li>—</li> : null}
      </ul>
      {detail.status !== 'draft' && detail.status !== 'returned' ? (
        <p className={classes.hint}>{k('lockedNote')}</p>
      ) : null}
      {detail.kind === 'defect_observation' && detail.defect ? (
        <p className={classes.hint}>
          {k('convertedTo', { no: detail.defect.defectNo })}{' '}
          {onOpenDefect ? (
            <Button
              size="compact-xs"
              variant="subtle"
              onClick={() => onOpenDefect(String(detail.defect?.id))}
            >
              {k('openDefect')}
            </Button>
          ) : null}
        </p>
      ) : null}
      <div className={classes.detailsActions}>
        {detail.kind === 'defect_observation' && !detail.defect && onConvert ? (
          <Button variant="light" leftSection={<IconTool size={16} />} onClick={onConvert}>
            {k('convertToDefect')}
          </Button>
        ) : null}
        {detail.can.edit ? (
          <Button
            variant="default"
            leftSection={<IconEdit size={16} />}
            onClick={() => onAction('edit')}
          >
            {k('edit')}
          </Button>
        ) : null}
        {detail.can.submit ? (
          <Button leftSection={<IconSend size={16} />} onClick={() => onAction('submit')}>
            {k('submit')}
          </Button>
        ) : null}
        {detail.can.approve ? (
          <Button leftSection={<IconCheck size={16} />} onClick={() => onAction('approve')}>
            {k('approve')}
          </Button>
        ) : null}
        {detail.can.return ? (
          <Button
            variant="default"
            leftSection={<IconArrowBackUp size={16} />}
            onClick={() => onAction('return')}
          >
            {k('return')}
          </Button>
        ) : null}
        {detail.can.reopen ? (
          <Button
            variant="default"
            leftSection={<IconLockOpen size={16} />}
            onClick={() => onAction('reopen')}
          >
            {k('reopen')}
          </Button>
        ) : null}
        {detail.can.discard ? (
          <Button
            variant="subtle"
            color="red"
            leftSection={<IconTrash size={16} />}
            onClick={() => onAction('discard')}
          >
            {k('discard')}
          </Button>
        ) : null}
      </div>
    </section>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </>
  );
}

/** Sefer Noon raporunun salt okunur paneli (ADR 0051 §1); "Open in Voyage" sefere götürür. */
export function NoonDetail({
  noon,
  onClose,
  onOpen,
}: {
  noon: DailyNoonReport;
  onClose: () => void;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`daily.detail.${key}`, options ?? {});
  const badge = noonBadge(noon.status);
  const position =
    noon.lat !== null && noon.lon !== null
      ? `${formatPosition(noon.lat, 'lat')}  ${formatPosition(noon.lon, 'lon')}`
      : '—';
  return (
    <section className={classes.card} aria-labelledby="daily-noon-title" data-panel>
      <div className={classes.detailsHead}>
        <span className={classes.kindIcon} data-kind="noon" aria-hidden>
          {KIND_ICONS.noon}
        </span>
        <h2 id="daily-noon-title" className={classes.detailsLabel}>
          {t('daily.kinds.noon')}
        </h2>
        <StatusBadge tone={badge.tone}>{t(`daily.reportStatuses.${badge.key}`)}</StatusBadge>
        <UnstyledButton className={classes.iconButton} onClick={onClose} aria-label={k('close')}>
          <IconX size={18} />
        </UnstyledButton>
      </div>
      <dl className={classes.facts}>
        <dt>{k('dateTime')}</dt>
        <dd>{shipDateTime(noon.reportedAt, noon.tzOffsetMin)}</dd>
        <dt>{k('voyage')}</dt>
        <dd>{noon.voyageNo}</dd>
        <dt>{k('position')}</dt>
        <dd>{position}</dd>
        <dt>{k('weather')}</dt>
        <dd>
          {noon.windBeaufort !== null ? k('windValue', { bf: noon.windBeaufort }) : '—'}
          {noon.condition ? ` · ${noon.condition}` : ''}
        </dd>
        <dt>{k('sea')}</dt>
        <dd>{noon.seaState !== null ? k('seaValue', { state: noon.seaState }) : '—'}</dd>
        <dt>{k('mainEngine')}</dt>
        <dd>{noon.meRpm !== null ? k('rpmValue', { rpm: noon.meRpm }) : '—'}</dd>
      </dl>
      <p className={classes.hint}>{t('daily.reports.noonHint')}</p>
      <div className={classes.detailsActions}>
        <Button variant="default" leftSection={<IconExternalLink size={16} />} onClick={onOpen}>
          {k('noonOpen')}
        </Button>
      </div>
    </section>
  );
}
